import { DEFAULT_DOMAIN, Domain, DomainManifest } from "@swivl-mono/types";
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DOMAIN_MANIFESTS } from "./Domain.Manifests";

const LOCAL_STORAGE_KEY = "swivl.aiStudio.activeDomain";

interface DomainContextValue {
  /** Current active Domain id (sourced from the :domain URL path segment) */
  activeDomain: Domain;
  /** Manifest for the active Domain */
  activeManifest: DomainManifest;
  /** All manifests — for rendering the selector */
  allManifests: DomainManifest[];
  /** Switch the active Domain — navigates to the same page under the new domain */
  setActiveDomain: (domain: Domain) => void;
  /** Build a domain-scoped AI Studio path for a given page */
  studioPath: (page: string, ...rest: (string | number | undefined)[]) => string;
}

const DomainContext = createContext<DomainContextValue | undefined>(undefined);

function isValidDomain(value: unknown): value is Domain {
  return typeof value === "string" && DOMAIN_MANIFESTS.some((m) => m.id === value);
}

/** Reads the persisted "last active" domain from localStorage. */
export function readPersistedDomain(): Domain {
  try {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(LOCAL_STORAGE_KEY) : null;
    if (isValidDomain(stored)) return stored;
  } catch {
    // localStorage disabled — fall through
  }
  return DEFAULT_DOMAIN;
}

/**
 * Extract the active domain from a pathname — looks for the `/ai-studio/<domain>/…`
 * pattern. Returns null if no match (e.g., the user is not on an AI Studio page).
 */
function domainFromPathname(pathname: string): Domain | null {
  const match = pathname.match(/^\/ai-studio\/([^/]+)/);
  if (!match) return null;
  return isValidDomain(match[1]) ? (match[1] as Domain) : null;
}

/**
 * DomainProvider — makes the URL's `/ai-studio/<domain>/` segment the source of
 * truth for the active Domain, and syncs it to localStorage so future loads
 * without a domain-in-URL remember the last selection.
 *
 * Hoisted above the global NavBar so any component (including NavBar-injected
 * widgets like DomainPicker) can call useDomain(). When the user is not on an
 * AI Studio page, `activeDomain` falls back to the persisted/default value.
 */
export function DomainProvider({ children }: { children: ReactNode }): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();

  const activeDomain: Domain = useMemo(() => {
    return domainFromPathname(location.pathname) ?? readPersistedDomain();
  }, [location.pathname]);

  // Persist the active domain to localStorage so bare /ai-studio redirects remember it
  useEffect(() => {
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, activeDomain);
    } catch {
      // non-fatal
    }
  }, [activeDomain]);

  const activeManifest = useMemo(() => DOMAIN_MANIFESTS.find((m) => m.id === activeDomain) ?? DOMAIN_MANIFESTS[0], [activeDomain]);

  const setActiveDomain = useCallback(
    (next: Domain) => {
      // Swap the :domain segment in the current URL with the new one, preserving everything else
      const newPath = location.pathname.replace(/^\/ai-studio\/[^/]+/, `/ai-studio/${next}`);
      navigate(newPath + location.search, { replace: false });
    },
    [navigate, location.pathname, location.search]
  );

  const studioPath = useCallback(
    (page: string, ...rest: (string | number | undefined)[]): string => {
      const segments = [page, ...rest.filter((s): s is string | number => s !== undefined && s !== "").map(String)];
      return `/ai-studio/${activeDomain}/${segments.join("/")}`;
    },
    [activeDomain]
  );

  const value: DomainContextValue = useMemo(
    () => ({
      activeDomain,
      activeManifest,
      allManifests: DOMAIN_MANIFESTS,
      setActiveDomain,
      studioPath
    }),
    [activeDomain, activeManifest, setActiveDomain, studioPath]
  );

  return <DomainContext.Provider value={value}>{children}</DomainContext.Provider>;
}

/**
 * Hook to read/write the active AI Studio Domain.
 * Must be called inside a DomainProvider.
 */
export function useDomain(): DomainContextValue {
  const ctx = useContext(DomainContext);
  if (!ctx) throw new Error("useDomain must be used inside a DomainProvider");
  return ctx;
}
